import { normalizeObjectType, OBJECT_TYPES, type CardinalDirection, type ObjectType, type Rect, type TileLayer, type TileMapObject, type Vector2, type WorldSpace } from './types/index';

/** One tile's step toward each side, in tile coordinates (north is -y). */
export const CARDINAL_STEPS: Readonly<Record<CardinalDirection, Vector2>> = {
    north: { x: 0, y: -1 },
    east: { x: 1, y: 0 },
    south: { x: 0, y: 1 },
    west: { x: -1, y: 0 },
};

/**
 * Convert a serialized property value to a boolean.
 * Returns `undefined` for unrecognized string values.
 */
export function toBooleanProperty(value: string | number | boolean | undefined): boolean | undefined {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value !== 0;
    }

    if (typeof value === 'string') {
        return value === 'true';
    }

    return undefined;
}

/** Return the world-space center of a tile at the given grid coordinates. */
export function resolveTileCenter(col: number, row: number): WorldSpace {
    return { x: col + 0.5, y: row + 0.5 };
}

/** The center of a rect (an area, a map object): halfway across it and halfway down. */
export function resolveRectCenter(rect: Rect): WorldSpace {
    return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
}

/** Whether two rects overlap; ones that only touch along an edge do not. */
export function isRectOverlapping(left: Rect, right: Rect): boolean {
    return left.x < right.x + right.width
        && left.x + left.width > right.x
        && left.y < right.y + right.height
        && left.y + left.height > right.y;
}

/** A body's box in world space: its collision box (offsets from its anchor) placed at `position`. */
export function toWorldBox(position: WorldSpace, collisionBox: Rect): Rect {
    return { x: position.x + collisionBox.x, y: position.y + collisionBox.y, width: collisionBox.width, height: collisionBox.height };
}

/** Map object types a player can interact with without an explicit `interactionKind`. */
const INTERACTABLE_OBJECT_TYPES: ReadonlySet<ObjectType> = new Set<ObjectType>([
    OBJECT_TYPES.EXIT,
    OBJECT_TYPES.PICKUP,
    OBJECT_TYPES.CRYSTAL,
    OBJECT_TYPES.DOOR,
    OBJECT_TYPES.TRIGGER,
]);

/**
 * Whether a player can interact with a map object: an interactable type (in
 * any case), or any object whose `interactionKind` is set and not `none`.
 * The host's rule; the client shows the interact cursor by the same one.
 */
export function isInteractableMapObject(object: Pick<TileMapObject, 'type' | 'properties'>): boolean {
    const interactionKind = object.properties.interactionKind;
    if (typeof interactionKind === 'string' && interactionKind.trim().toLowerCase() !== 'none') {
        return true;
    }
    return INTERACTABLE_OBJECT_TYPES.has(normalizeObjectType(object.type));
}

/** A layer's gid at a tile: 0 off the layer, or where it has none. */
export function readLayerGid(layer: Readonly<TileLayer>, tile: Vector2): number {
    const { x, y } = tile;
    if (x < 0 || y < 0 || x >= layer.width || y >= layer.height) {
        return 0;
    }
    return layer.data[y * layer.width + x] ?? 0;
}

/**
 * Whether a tile is floor: ground with no wall on it, where characters
 * stand and what every view of a map draws as floor. The wall layer can be
 * smaller than the ground; off its edge there is no wall.
 */
export function isFloorAt(ground: Readonly<TileLayer>, walls: Readonly<TileLayer> | undefined, tile: Vector2): boolean {
    return readLayerGid(ground, tile) !== 0 && (walls === undefined || readLayerGid(walls, tile) === 0);
}
